import { isDaycareOpenThisDay } from '@/modules/helper/daycareHelper'
import { Daycare } from '@/types/Daycare'
import { NewOccasionalSlot } from '@/types/Slots'
import { OccasionalSlot, OccasionalSlotProps } from './occasionalSlot'
import { verifyConfirmedChildNumber } from './common'
import { DateSelection } from './dateSelection'

import {
    areSelectedDatesWellFormed,
    getOccasionalSlotsErrors,
    OccasionalSlotsValidationErrors,
    OccasionalSlotsValidationProps,
    resolveDatesForDaycare
} from './occasionalSlotsValidation'

export type GenerateNewOccasionalSlotsProps = Omit<OccasionalSlotProps, 'date' | 'childId'>

export class NewOccasionalSlotsBuilder {
    daycare: Daycare

    constructor(daycare: Daycare) {
        this.daycare = daycare
    }

    getErrors(props: OccasionalSlotsValidationProps): OccasionalSlotsValidationErrors {
        return getOccasionalSlotsErrors(this.daycare, props)
    }

    isSlotValid({ slotProps, dates }: { slotProps: GenerateNewOccasionalSlotsProps; dates: DateSelection }): boolean {
        try {
            if (!areSelectedDatesWellFormed(dates)) return false
            const resolvedDays: string[] = resolveDatesForDaycare(this.daycare, dates)

            return (
                resolvedDays.length > 0 &&
                (dates.mode !== 'single' || isDaycareOpenThisDay(this.daycare, { day: dates.date })) &&
                resolvedDays.every((date: string): boolean =>
                    OccasionalSlot.isSlotValid(this.daycare, { ...slotProps, date, childId: undefined })
                )
            )
        } catch {
            return false
        }
    }

    generate({
        slotProps: props,
        count,
        dates,
        confirmedChildIds,
        isScheduled
    }: {
        slotProps: GenerateNewOccasionalSlotsProps
        count: number
        dates: DateSelection
        confirmedChildIds: number[]
        isScheduled: boolean
    }): NewOccasionalSlot[] {
        const { daycare } = this
        verifyConfirmedChildNumber(count, confirmedChildIds)

        const resolvedDays: string[] = resolveDatesForDaycare(daycare, dates)
        const resolvedSlots: NewOccasionalSlot[] = []
        for (const resolvedDay of resolvedDays) {
            for (let i: number = 0; i < count; i++) {
                const confirmedChildId: number | undefined = confirmedChildIds[i]
                resolvedSlots.push(
                    this.toInput(
                        new OccasionalSlot(
                            daycare,
                            { ...props, date: resolvedDay, childId: confirmedChildId },
                            isScheduled
                        )
                    )
                )
            }
        }
        return resolvedSlots
    }

    toInput(occasionalSlot: OccasionalSlot): NewOccasionalSlot {
        const {
            date,
            start,
            end,
            childId,
            sectionId,
            childrenPriority,
            groupPriority,
            scheduled,
            expiry,
            internalNote,
            externalNote,
            externalIcon,
            placeId
        } = occasionalSlot
        return {
            date,
            start,
            end,
            childId,
            sectionId,
            childrenPriority,
            groupPriority,
            scheduled,
            expiry,
            internalNote,
            externalNote,
            externalIcon,
            placeId
        }
    }
}
